import { lazy, Suspense, useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { FaGithub, FaTelegram, FaDiscord, FaGlobe } from 'react-icons/fa'
import { SiModrinth } from 'react-icons/si'
import { ArrowDownRight, MapPin } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import WeatherWidget from '../ui/WeatherWidget'
import { getDefaultDiscordAvatarUrl, getDiscordAvatarUrl } from '../../config/discord'
const GitHubGraph = lazy(() => import('../ui/GitHubGraph'))

function useTypingEffect(words, typingSpeed = 80, deletingSpeed = 50, pauseTime = 2000) {
  const [display, setDisplay] = useState('')
  const [wordIndex, setWordIndex] = useState(0)
  const [charIndex, setCharIndex] = useState(0)
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    const current = words[wordIndex] || ''
    let timeout

    if (!isDeleting && charIndex < current.length) {
      timeout = setTimeout(() => {
        setDisplay(current.slice(0, charIndex + 1))
        setCharIndex(charIndex + 1)
      }, typingSpeed)
    } else if (!isDeleting && charIndex === current.length) {
      timeout = setTimeout(() => setIsDeleting(true), pauseTime)
    } else if (isDeleting && charIndex > 0) {
      timeout = setTimeout(() => {
        setDisplay(current.slice(0, charIndex - 1))
        setCharIndex(charIndex - 1)
      }, deletingSpeed)
    } else if (isDeleting && charIndex === 0) {
      setIsDeleting(false)
      setWordIndex((wordIndex + 1) % words.length)
    }

    return () => clearTimeout(timeout)
  }, [charIndex, isDeleting, wordIndex, words, typingSpeed, deletingSpeed, pauseTime])

  return display
}

export default function HeroSection() {
  const { t } = useTranslation()
  const roles = t('roles', { returnObjects: true })
  const typedRole = useTypingEffect(Array.isArray(roles) ? roles : [])
  const [showCopied, setShowCopied] = useState(false)
  const [discordStatus, setDiscordStatus] = useState('offline')
  const [avatar, setAvatar] = useState('/avatar.webp')

  const handleDiscordClick = (e) => {
    e.preventDefault()
    navigator.clipboard.writeText('sqrilizz')
    setShowCopied(true)
    setTimeout(() => setShowCopied(false), 2000)
  }

  const statusColors = {
    online: '#43b581',
    idle: '#faa61a',
    dnd: '#f04747',
    offline: '#747f8d'
  }

  useEffect(() => {
    const handleDiscordUpdate = (event) => {
      const data = event.detail
      if (data.discord_status) setDiscordStatus(data.discord_status)
      if (data.discord_user) {
        setAvatar(
          getDiscordAvatarUrl(data.discord_user, 128)
          || getDefaultDiscordAvatarUrl(data.discord_user, 128)
          || '/avatar.webp',
        )
      }
    }
    window.addEventListener('discord-update', handleDiscordUpdate)
    return () => window.removeEventListener('discord-update', handleDiscordUpdate)
  }, [])

  const socials = [
    { icon: FaGithub, href: 'https://github.com/Sqrilizz', label: 'GitHub' },
    { icon: FaTelegram, href: 'https://t.me/sqrilizz', label: 'Telegram' },
    { icon: FaDiscord, href: '#', onClick: handleDiscordClick, label: 'Discord' },
    { icon: SiModrinth, href: 'https://modrinth.com/user/Sqrilizz', label: 'Modrinth' },
    { icon: FaGlobe, href: 'https://sqrilizz.tech', label: 'sqrilizz.tech' },
  ]

  return (
    <section className="min-h-[78dvh] flex items-center px-6 py-16 sm:py-20">
      <div className="max-w-3xl mx-auto w-full">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="flex flex-col items-start gap-5"
        >
          <div className="relative">
            <img
              src={avatar}
              alt="Matvei"
              width="80"
              height="80"
              fetchPriority="high"
              className="w-20 h-20 rounded-2xl border border-zinc-800"
              onError={() => setAvatar('/avatar.webp')}
            />
            <div
              className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full border-[2.5px] border-zinc-950"
              style={{ backgroundColor: statusColors[discordStatus] }}
            />
          </div>

          <div>
            <h1 className="hero-name text-5xl md:text-6xl font-bold tracking-tight text-white">
              Matvei
            </h1>
            <p className="hero-role text-violet-400 text-sm mt-2 min-h-5">
              {typedRole}<span className="animate-pulse">|</span>
            </p>
            <p className="text-zinc-400 text-lg mt-3 leading-relaxed max-w-lg">
              {t('heroTagline')}
            </p>
          </div>

          <div className="flex items-center gap-2 text-sm text-zinc-500">
            <MapPin size={14} aria-hidden="true" />
            <span>Tallinn, Estonia</span>
            <span className="text-zinc-700 mx-1">·</span>
            <WeatherWidget />
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <a
              href="#projects"
              className="inline-flex items-center gap-1.5 text-sm text-violet-400 transition-colors hover:text-violet-200"
            >
              {t('heroCta')}
              <ArrowDownRight size={15} aria-hidden="true" />
            </a>

            <div className="flex items-center gap-2 relative">
            {socials.map((s, i) => (
              s.onClick ? (
                <motion.button
                  key={i}
                  type="button"
                  onClick={s.onClick}
                  aria-label={t('copyDiscord')}
                  className="w-10 h-10 rounded-xl bg-zinc-900/80 border border-zinc-800 flex items-center justify-center text-zinc-500 hover:text-white hover:border-zinc-700 transition-all duration-200"
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.95 }}
                ><s.icon size={17} aria-hidden="true" /></motion.button>
              ) : <motion.a
                key={i}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.label}
                className="w-10 h-10 rounded-xl bg-zinc-900/80 border border-zinc-800 flex items-center justify-center text-zinc-500 hover:text-white hover:border-zinc-700 transition-all duration-200"
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.95 }}
              ><s.icon size={17} aria-hidden="true" /></motion.a>
            ))}

            {showCopied && (
              <motion.span
                initial={{ opacity: 0, x: -5 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0 }}
                className="text-xs text-emerald-400 ml-2"
              >
                {t('copied')}
              </motion.span>
            )}
            </div>
          </div>

          <div className="flex items-start gap-6 w-full">
            <Suspense fallback={<div className="h-32 w-full rounded-xl bg-zinc-900/40" />}>
              <GitHubGraph />
            </Suspense>
          </div>

        </motion.div>
      </div>
    </section>
  )
}
